import React from "react";
import {
  Box,
  Flex,
  HStack,
  Image,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import {
  FaHome,
  FaHeadphones,
  FaCalculator,
  FaCalendarAlt,
  FaSignOutAlt,
  FaTruck,
} from "react-icons/fa";
import { useAppContext } from "../context/AppContext";
import { resetWorkflowSession } from "../utils/workflowSession";
import { useNavigate, useLocation } from "react-router-dom";
import imagelogo from "../images/trr_cenze_comrcio_de_combustveis_e_derivados_logo.png";

const UserDataHome = () => {
  const { setUsername, setPassword, setGlobalToken } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Função para sair (limpar dados e redirecionar)
  const handleLogout = () => {
    setUsername("");
    setPassword("");
    setGlobalToken("");
    localStorage.removeItem("portal_username");
    localStorage.removeItem("portal_password");
    localStorage.removeItem("portal_token_payload");
    localStorage.removeItem("portal_refresh_token");
    sessionStorage.clear();
    resetWorkflowSession();
    navigate("/");
  };

  // Verificar se a rota está ativa
  const isActive = (path) => {
    if (path === "/home") {
      return location.pathname === "/home" || location.pathname === "/";
    }
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  return (
    <Box
      as="header"
      bg="white"
      borderBottom="1px solid"
      borderColor="gray.200"
      boxShadow="sm"
      position="sticky"
      top={0}
      zIndex={1000}
      w="100%"
      overflow="hidden"
    >
      {/* Ondas Animadas de Fundo */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        width="100%"
        height="100%"
        zIndex={0}
        opacity={0.12}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 100"
          preserveAspectRatio="none"
          style={{
            width: "100%",
            height: "100%",
            display: "block",
          }}
        >
          <defs>
            <linearGradient
              id="waveGradient1"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#007abe" stopOpacity="0.8" />
              <stop offset="50%" stopColor="#007abe" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#007abe" stopOpacity="0.8" />
            </linearGradient>
            <linearGradient
              id="waveGradient2"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#c0a316" stopOpacity="0.2" />
              <stop offset="50%" stopColor="#c0a316" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#c0a316" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          {/* Onda 1 - Azul */}
          <path
            fill="url(#waveGradient1)"
            d="M0,50 Q360,20 720,50 T1440,50 L1440,100 L0,100 Z"
          >
            <animate
              attributeName="d"
              dur="12s"
              repeatCount="indefinite"
              values="M0,50 Q360,20 720,50 T1440,50 L1440,100 L0,100 Z;M0,50 Q360,80 720,50 T1440,50 L1440,100 L0,100 Z;M0,50 Q360,20 720,50 T1440,50 L1440,100 L0,100 Z"
              calcMode="spline"
              keyTimes="0;0.5;1"
              keySplines="0.4 0 0.2 1;0.4 0 0.2 1"
            />
          </path>
          {/* Onda 2 - Dourado (mais lenta) */}
          <path
            fill="url(#waveGradient2)"
            d="M0,70 Q480,40 960,70 T1440,70 L1440,100 L0,100 Z"
            opacity="0.7"
          >
            <animate
              attributeName="d"
              dur="15s"
              repeatCount="indefinite"
              values="M0,70 Q480,40 960,70 T1440,70 L1440,100 L0,100 Z;M0,70 Q480,100 960,70 T1440,70 L1440,100 L0,100 Z;M0,70 Q480,40 960,70 T1440,70 L1440,100 L0,100 Z"
              calcMode="spline"
              keyTimes="0;0.5;1"
              keySplines="0.4 0 0.2 1;0.4 0 0.2 1"
            />
          </path>
        </svg>
      </Box>

      <Box
        maxW={{ base: "100%", lg: "1400px" }}
        mx="auto"
        px={{ base: 4, md: 8 }}
        py={{ base: 3, md: 4 }}
        w="100%"
        position="relative"
        zIndex={1}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "center", md: "flex-end" }}
          justify="space-between"
          gap={{ base: 4, md: 6 }}
          w="100%"
        >
          {/* Logo da Empresa - Totalmente à Esquerda */}
          <Box
            alignSelf={{ base: "center", md: "flex-end" }}
            flexShrink={0}
            cursor="pointer"
            onClick={() => navigate("/home")}
            _hover={{
              opacity: 0.8,
              transform: "scale(1.05)",
            }}
            transition="all 0.2s"
          >
            <Image
              src={imagelogo}
              alt="Cenze Logo"
              maxW={{ base: "120px", md: "150px" }}
              maxH={{ base: "60px", md: "80px" }}
              objectFit="contain"
            />
          </Box>

          {/* Ícones de Navegação - Centro */}
          <HStack
            spacing={3}
            align="flex-end"
            display={{ base: "none", md: "flex" }}
            flex="1"
            justify="center"
            mx={{ base: 0, md: 8 }}
          >
            <Tooltip label="Home" placement="bottom">
              <IconButton
                icon={<FaHome />}
                aria-label="Home"
                onClick={() => navigate("/home")}
                bg={isActive("/home") ? "#007abe" : "gray.100"}
                color={isActive("/home") ? "white" : "#007abe"}
                _hover={{
                  bg: "#007abe",
                  color: "white",
                  transform: "translateY(-2px)",
                  boxShadow: "md",
                }}
                transition="all 0.2s"
                borderRadius="lg"
                size="md"
                boxSize="40px"
              />
            </Tooltip>

            <Tooltip label="Atendimento" placement="bottom">
              <IconButton
                icon={<FaHeadphones />}
                aria-label="Atendimento"
                onClick={() => navigate("/atendimento")}
                bg={isActive("/atendimento") ? "#007abe" : "gray.100"}
                color={isActive("/atendimento") ? "white" : "#007abe"}
                _hover={{
                  bg: "#007abe",
                  color: "white",
                  transform: "translateY(-2px)",
                  boxShadow: "md",
                }}
                transition="all 0.2s"
                borderRadius="lg"
                size="md"
                boxSize="40px"
              />
            </Tooltip>

            <Tooltip label="Simulação de Custos" placement="bottom">
              <IconButton
                icon={<FaCalculator />}
                aria-label="Simulação de Custos"
                onClick={() => navigate("/simulacao-custos")}
                bg={isActive("/simulacao-custos") ? "#007abe" : "gray.100"}
                color={isActive("/simulacao-custos") ? "white" : "#007abe"}
                _hover={{
                  bg: "#007abe",
                  color: "white",
                  transform: "translateY(-2px)",
                  boxShadow: "md",
                }}
                transition="all 0.2s"
                borderRadius="lg"
                size="md"
                boxSize="40px"
              />
            </Tooltip>

            <Tooltip label="Ordem de Carregamento" placement="bottom">
              <IconButton
                icon={<FaTruck />}
                aria-label="Ordem de Carregamento"
                onClick={() => navigate("/ordem-carregamento")}
                bg={isActive("/ordem-carregamento") ? "#007abe" : "gray.100"}
                color={isActive("/ordem-carregamento") ? "white" : "#007abe"}
                _hover={{
                  bg: "#007abe",
                  color: "white",
                  transform: "translateY(-2px)",
                  boxShadow: "md",
                }}
                transition="all 0.2s"
                borderRadius="lg"
                size="md"
                boxSize="40px"
              />
            </Tooltip>

            <Tooltip label="Agenda" placement="bottom">
              <IconButton
                icon={<FaCalendarAlt />}
                aria-label="Agenda"
                onClick={() => navigate("/agenda")}
                bg={isActive("/agenda") ? "#007abe" : "gray.100"}
                color={isActive("/agenda") ? "white" : "#007abe"}
                _hover={{
                  bg: "#007abe",
                  color: "white",
                  transform: "translateY(-2px)",
                  boxShadow: "md",
                }}
                transition="all 0.2s"
                borderRadius="lg"
                size="md"
                boxSize="40px"
              />
            </Tooltip>
          </HStack>

          {/* Botão SAIR - Totalmente à Direita */}
          <Box flexShrink={0} alignSelf={{ base: "center", md: "flex-end" }}>
            <Tooltip label="Sair" placement="bottom">
              <IconButton
                icon={<FaSignOutAlt />}
                aria-label="Sair"
                onClick={handleLogout}
                bg="blue.500"
                color="white"
                _hover={{
                  bg: "red.600",
                  transform: "translateY(-2px)",
                  boxShadow: "md",
                }}
                transition="all 0.2s"
                borderRadius="lg"
                size="md"
                boxSize="40px"
              />
            </Tooltip>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default UserDataHome;
